import {
  DIGEST_COMMENT_MAX_CHARS,
  DIGEST_MAX_CHARS,
  type DigestVersion,
} from '@mss_creator/shared';
import { Alert, Button, TextArea } from '@pms-digital/design-system';
import { type ReactNode, useId, useState } from 'react';

/**
 * One reviewable digest — a file's, or the matrix's cross-document synthesis.
 *
 * Purely presentational: it owns the comment field, the hand-edit form and
 * their client-side validation, and nothing else. The mutations, the query
 * invalidation and the French failure copy stay with the owner
 * (`FileDigestCard`, `SynthesisDigestCard`), because the two levels hit
 * different routes and have different preconditions even though they read
 * identically to the user.
 *
 * A digest and a synthesis are structurally the same three fields, so both are
 * accepted through `DigestVersionSummary` rather than duplicating this card.
 */

/** A comment is a property of the version it produced, never a standing instruction (D18). */
export type DigestVersionSummary = Pick<DigestVersion, 'content' | 'comment' | 'origin'>;

function formatCount(count: number): string {
  return new Intl.NumberFormat('fr-FR').format(count);
}

/** The inline refusal for an over-long comment, shared by every place that takes one (issue #20). */
export function commentTooLongMessage(length: number): string {
  return `Le commentaire ne peut pas dépasser ${formatCount(DIGEST_COMMENT_MAX_CHARS)} caractères (actuellement ${formatCount(length)}).`;
}

export interface DigestVersionCardProps {
  /** Heading — a filename at file level, a fixed label for the synthesis. */
  title: string;
  /**
   * A French noun phrase naming what is being acted on ("le digest de
   * brief.pdf", "la synthèse"). Used to build accessible button names that
   * stay unambiguous when several cards are on screen at once.
   */
  subject: string;
  /** Null when this level has never been digested — the review surface's normal starting point. */
  version: DigestVersionSummary | null;
  /** Total versions at this level, when the caller knows it. Shown only once there is a history worth mentioning. */
  versionCount?: number;
  /** Rendered between the heading and the content — the synthesis staleness warning goes here. */
  notice?: ReactNode;
  /** Shown next to the origin chip — "À redérouler" / "À refaire" when the framing moved under this digest. */
  badge?: ReactNode;
  /** What to say instead of the content when `version` is null. */
  emptyLabel: string;
  /** Label of the hand-edit textarea — "Texte du digest" / "Texte de la synthèse". */
  editFieldLabel: string;
  disabled: boolean;
  /**
   * Blocks the model-calling button alone, leaving "Modifier" usable — the
   * matrix has no sujet/contexte, which the three generating routes refuse
   * (issue #1). Writing a digest by hand never reads the framing, so it stays
   * available: this is a locked door, not a locked room.
   */
  regenerateDisabled?: boolean;
  regeneratePending: boolean;
  editPending: boolean;
  /** Per-cause French failure message for the owner's last mutation, if it failed. */
  error: string | null;
  /** Rejecting keeps the comment in its field, next to the message the owner renders. */
  onRegenerate: (comment: string | null) => Promise<unknown>;
  /** Rejecting keeps the edit form open, next to the message the owner renders. */
  onEdit: (content: string) => Promise<unknown>;
}

export function DigestVersionCard({
  title,
  subject,
  version,
  versionCount,
  notice,
  badge,
  emptyLabel,
  editFieldLabel,
  disabled,
  regenerateDisabled = false,
  regeneratePending,
  editPending,
  error,
  onRegenerate,
  onEdit,
}: DigestVersionCardProps) {
  const draftId = useId();
  const [editing, setEditing] = useState(false);
  const [comment, setComment] = useState('');
  const [commentFocused, setCommentFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const [validation, setValidation] = useState<string | null>(null);

  const pending = regeneratePending || editPending;
  const locked = disabled || pending;
  // Nothing has been digested yet, so "regenerate" would read as a lie.
  const regenerateLabel = version ? 'Régénérer' : 'Générer';
  // One line at rest, three while it is being written in or holds text (issue #20).
  const commentExpanded = commentFocused || comment.length > 0;

  function openEdit() {
    setValidation(null);
    setDraft(version?.content ?? '');
    setEditing(true);
  }

  async function submitRegenerate() {
    const trimmed = comment.trim();
    if (trimmed.length > DIGEST_COMMENT_MAX_CHARS) {
      setValidation(commentTooLongMessage(trimmed.length));
      return;
    }
    setValidation(null);
    try {
      await onRegenerate(trimmed.length > 0 ? trimmed : null);
      // The comment now lives on the version it produced; the field starts
      // empty again so nothing carries over to the next generation (D18).
      setComment('');
    } catch {
      // Handled, not swallowed: the owner's mutation state carries the failure
      // and renders it through `error` below. The comment stays in its field —
      // retyping it after a transient model failure would be the worst
      // possible moment to lose it.
    }
  }

  async function submitEdit() {
    const trimmed = draft.trim();
    if (trimmed.length === 0) {
      setValidation('Le texte ne peut pas être vide.');
      return;
    }
    if (trimmed.length > DIGEST_MAX_CHARS) {
      setValidation(
        `Le texte ne peut pas dépasser ${formatCount(DIGEST_MAX_CHARS)} caractères (actuellement ${formatCount(trimmed.length)}).`,
      );
      return;
    }
    setValidation(null);
    try {
      await onEdit(trimmed);
      setEditing(false);
    } catch {
      // See the note in `submitRegenerate` — the draft is kept on screen.
    }
  }

  return (
    <div className="rounded-md border border-gray-200 p-3">
      <div className="flex items-baseline justify-between gap-3">
        <p className="min-w-0 truncate font-semibold text-small">{title}</p>
        <div className="flex shrink-0 items-center gap-2">
          {badge}
          {version ? (
            <span className="rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-700">
              {version.origin === 'manual' ? 'Modifié à la main' : 'Généré'}
              {versionCount !== undefined && versionCount > 1
                ? ` · ${formatCount(versionCount)} versions`
                : ''}
            </span>
          ) : (
            <span className="text-caption text-gray-500">Aucun digest</span>
          )}
        </div>
      </div>

      {notice}

      {version ? (
        <div className="mt-2 max-h-48 overflow-y-auto">
          <p className="whitespace-pre-wrap text-gray-700 text-small">{version.content}</p>
        </div>
      ) : (
        <p className="mt-2 text-gray-600 text-small">{emptyLabel}</p>
      )}

      {/* The comment is a property of the version it produced, never a standing
          instruction (D18) — so it is shown attached to that version, and the
          comment field below always starts empty. */}
      {version?.comment && (
        <p className="mt-2 text-caption text-gray-600">Commentaire utilisé : {version.comment}</p>
      )}

      {/* Always on screen, no step to open it: the field sits beside the button
          that sends it, and the button leaves with whatever the field holds,
          empty or not (issue #20). */}
      <div className="mt-3 flex items-start gap-2">
        <TextArea
          label={<span className="sr-only">{`Commentaire pour ${subject}`}</span>}
          placeholder="Commentaire pour cette génération (facultatif)"
          rows={commentExpanded ? 3 : 1}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
          onFocus={() => setCommentFocused(true)}
          onBlur={() => setCommentFocused(false)}
          disabled={locked || regenerateDisabled}
        />
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => void submitRegenerate()}
          disabled={locked || regenerateDisabled}
          aria-label={`${regenerateLabel} ${subject}`}
        >
          {regenerateLabel}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={editing ? () => setEditing(false) : openEdit}
          disabled={locked}
          aria-label={editing ? `Annuler : ${subject}` : `Modifier ${subject}`}
        >
          {editing ? 'Annuler' : 'Modifier'}
        </Button>
      </div>
      {commentExpanded && (
        <p className="mt-1 text-caption text-gray-600">
          S’applique à cette génération uniquement, puis le champ se vide. Entrée : retour à la
          ligne.
        </p>
      )}

      {editing && (
        <div className="mt-3">
          <TextArea
            label={editFieldLabel}
            id={draftId}
            status={validation ? 'error' : 'default'}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            disabled={locked}
            aria-invalid={validation !== null}
          />
          <p className="mt-1 text-caption text-gray-600">
            {formatCount(draft.length)} / {formatCount(DIGEST_MAX_CHARS)} caractères
          </p>
          <Button
            variant="secondary"
            type="button"
            size="sm"
            onClick={() => void submitEdit()}
            disabled={locked}
            aria-label={`Enregistrer ${subject}`}
          >
            Enregistrer
          </Button>
        </div>
      )}

      {/* A regeneration is a synchronous model call that routinely takes more
          than a minute (see `digestsApi`). Saying so is the difference between
          "it's working" and "it's broken". */}
      {regeneratePending && (
        <output className="mt-2 block text-gray-600 text-small">
          Génération en cours — un appel au modèle prend généralement une à deux minutes.
        </output>
      )}
      {editPending && (
        <output className="mt-2 block text-gray-600 text-small">Enregistrement…</output>
      )}

      {validation && <Alert tone="danger">{validation}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
    </div>
  );
}
